export type SpinnerProps = {
  size?: number;
  className?: string;
} & (
  | {
      /** Announced label; a spinner without one is a silent wait (03-components §12). */
      label: string;
      decorative?: false;
    }
  | {
      /**
       * Hidden from assistive technology: the wait is announced by a live region the spinner sits in, whose own words say
       * what is under way — a second status inside it would be announced twice, or, mounted and unmounted with each
       * state, not reliably at all (01-foundations §7.3).
       */
      decorative: true;
      label?: undefined;
    }
);

/** Indeterminate progress. Frozen under reduced motion by components.css. */
export function Spinner({ size = 20, label, decorative, className }: SpinnerProps) {
  if (decorative) {
    return <span className={['spinner', className].filter(Boolean).join(' ')} aria-hidden="true" style={{ inlineSize: size, blockSize: size }} />;
  }
  return (
    <span className={['spinner', className].filter(Boolean).join(' ')} role="status" aria-live="polite" style={{ inlineSize: size, blockSize: size }}>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

export interface SkeletonProps {
  /** CSS length; defaults to the full line. */
  width?: string;
  /** CSS length; defaults to one text line. */
  height?: string;
  shape?: 'line' | 'block' | 'circle';
  className?: string;
}

/** A shape-matching placeholder (03-components §12); purely decorative. */
export function Skeleton({ width, height, shape = 'line', className }: SkeletonProps) {
  return (
    <span
      className={['skeleton', `skeleton--${shape}`, className].filter(Boolean).join(' ')}
      style={{ inlineSize: width, blockSize: height }}
      aria-hidden="true"
    />
  );
}
